import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowLeft, Copy, DownloadSimple } from '@phosphor-icons/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminSpaceLoggingService, {
  RunLogDetailResponse,
  RunLogStatus,
} from '../../../service/AdminSpaceLoggingService';
import RunErrorSummary from './RunErrorSummary';
import RunMarkdownView from './RunMarkdownView';
import { downloadRunLogJson, downloadRunLogMarkdown } from './downloadRunLog';
import { buildRunLogMarkdown } from './runLogMarkdown';

const LIVE_POLL_INTERVAL_MS = 2000;

const isTerminalStatus = (status: string): boolean =>
  status === 'success' || status === 'error';

const statusChipColor = (
  status: string,
): 'success' | 'error' | 'info' | 'default' => {
  if (status === 'error') return 'error';
  if (status === 'success') return 'success';
  if (status === 'in_progress') return 'info';
  return 'default';
};

const statusChipLabel = (status: RunLogStatus | string): string =>
  status === 'in_progress' ? 'in progress' : status;

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text).catch(err => {
    console.error('Failed to copy to clipboard', err);
  });
};

const shortId = (value: string): string =>
  value.length > 14 ? `${value.slice(0, 8)}…${value.slice(-4)}` : value;

const formatEndTime = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

interface ProcessHeaderCellProps {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}

const ProcessHeaderCell: React.FC<ProcessHeaderCellProps> = ({
  label,
  value,
  mono,
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant='caption'
      sx={{
        display: 'block',
        color: '#9ca3af',
        fontSize: 10,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        fontWeight: 600,
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        color: '#111827',
        fontWeight: 500,
        fontFamily: mono
          ? 'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace'
          : undefined,
        fontSize: mono ? 12.5 : 13.5,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const SpaceLoggingDetailPage: React.FC = () => {
  const { processId } = useParams<{ processId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<RunLogDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!processId) return;
    let cancelled = false;
    const stopPolling = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
    const refetch = async () => {
      try {
        const res = await AdminSpaceLoggingService.getRun(processId);
        if (cancelled) return;
        setDetail(res);
        if (isTerminalStatus(res.status)) stopPolling();
      } catch (err) {
        if (cancelled) return;
        console.error('Run log poll failed', {
          processId,
          message: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    };
    setLoading(true);
    AdminSpaceLoggingService.getRun(processId)
      .then(res => {
        if (cancelled) return;
        setDetail(res);
        if (!isTerminalStatus(res.status)) {
          pollTimerRef.current = setInterval(refetch, LIVE_POLL_INTERVAL_MS);
        }
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.message ?? 'Failed to load run log');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [processId]);
  const doc = detail?.logData;
  const processIdDisplay = useMemo(
    () => (doc ? shortId(doc.processId) : ''),
    [doc],
  );
  const backToLogsUrl = useMemo(() => {
    if (!doc) return '/admin/space-logging';
    const params = new URLSearchParams();
    if (doc.orgId) params.set('orgId', doc.orgId);
    if (doc.spaceId) params.set('spaceId', doc.spaceId);
    const query = params.toString();
    return query ? `/admin/space-logging?${query}` : '/admin/space-logging';
  }, [doc]);
  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !doc) {
    return (
      <Box sx={{ p: 4 }}>
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate(backToLogsUrl)}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Back to logs
        </Button>
        <Typography color='error'>{error ?? 'Run log not found.'}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ background: '#f5f7fa', minHeight: '100vh' }}>
      <Box
        sx={{
          background: '#fff',
          borderBottom: '1px solid #e5e7eb',
          px: 3,
          py: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Button
            size='small'
            startIcon={<ArrowLeft size={14} />}
            onClick={() => navigate(backToLogsUrl)}
            sx={{ textTransform: 'none', color: '#4b5563' }}
          >
            Back to logs
          </Button>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                'minmax(120px, auto) minmax(100px, auto) minmax(100px, auto) minmax(120px, auto)',
              columnGap: 3,
              rowGap: 0.25,
              flex: 1,
              minWidth: 0,
            }}
          >
            <ProcessHeaderCell
              label='Process ID'
              mono
              value={
                <Box
                  component='span'
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                  }}
                >
                  {processIdDisplay}
                  <Tooltip title='Copy process id'>
                    <IconButton
                      size='small'
                      onClick={() => copyToClipboard(doc.processId)}
                      sx={{ p: 0.25 }}
                    >
                      <Copy size={11} />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
            <ProcessHeaderCell
              label='Org'
              value={doc.orgName ?? doc.orgId ?? '—'}
            />
            <ProcessHeaderCell
              label='Space'
              value={doc.spaceName ?? doc.spaceId}
            />
            <ProcessHeaderCell
              label={doc.status === 'in_progress' ? 'Started' : 'End time'}
              value={formatEndTime(
                doc.status === 'in_progress' ? doc.startedAt : doc.endedAt,
              )}
            />
          </Box>
          {doc.status === 'in_progress' && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                color: '#0369a1',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#0ea5e9',
                  animation: 'sc-live-pulse 1.2s ease-in-out infinite',
                  '@keyframes sc-live-pulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.4, transform: 'scale(0.85)' },
                  },
                }}
              />
              Live
            </Box>
          )}
          <Chip
            label={statusChipLabel(doc.status)}
            color={statusChipColor(doc.status)}
            size='small'
            sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 10.5 }}
          />
          <Button
            size='small'
            variant='outlined'
            startIcon={<DownloadSimple size={14} />}
            onClick={e => setExportAnchor(e.currentTarget)}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
          <Menu
            open={!!exportAnchor}
            anchorEl={exportAnchor}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                downloadRunLogMarkdown(doc);
                setExportAnchor(null);
              }}
            >
              Download full run as Markdown (.md)
            </MenuItem>
            <MenuItem
              onClick={() => {
                downloadRunLogJson(doc);
                setExportAnchor(null);
              }}
            >
              Download full run as JSON (.json)
            </MenuItem>
            <MenuItem
              onClick={() => {
                copyToClipboard(buildRunLogMarkdown(doc));
                setExportAnchor(null);
              }}
            >
              Copy Markdown to clipboard
            </MenuItem>
            <MenuItem
              onClick={() => {
                copyToClipboard(JSON.stringify(doc, null, 2));
                setExportAnchor(null);
              }}
            >
              Copy JSON to clipboard
            </MenuItem>
          </Menu>
        </Box>
        {doc.errorMessage && (
          <Box
            sx={{
              mt: 1.5,
              px: 1.5,
              py: 1,
              background: '#fef2f2',
              borderLeft: '3px solid #dc2626',
              borderRadius: 0.5,
            }}
          >
            <Typography variant='body2' sx={{ color: '#991b1b' }}>
              {doc.errorMessage}
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ px: 3, py: 3, maxWidth: 1280, mx: 'auto' }}>
        <RunErrorSummary doc={doc} />
        <RunMarkdownView doc={doc} />
      </Box>
    </Box>
  );
};

export default SpaceLoggingDetailPage;
